// Tourism cart (סל תיירות): a Storefront API cart, separate from the Xtra session cart (/cart).
// It only ever holds Experiences, its checkout is a guest checkout, and the Xtra cart drawer /
// login gate never see it. Loaded on every page of layout/tayarot.liquid (header count).
// Tokenless, same-origin Storefront API: a storefront token belongs to a sales channel, and the
// one marketing.liquid uses is the "brands" channel — orders made with it were filed under brands.
const { version } = window.TAYAROT.storefront;
const KEY = window.TAYAROT.demo ? 'tayarot_cart_id_demo' : 'tayarot_cart_id'; // demo mode has its own cart
// The Activity day is the variant (ADR-0009); DATE_ATTR / BRANCH_ATTR are read only on lines added before that.
export const DATE_ATTR = 'תאריך פעילות';
// The Region of the picked day — tells the server which Branch the voucher page navigates to.
export const REGION_ATTR = '_tourism_region';
export const BRANCH_ATTR = 'סניף';
// Where the voucher goes — asked on the cart page, read by the server (NestApp tourism-notifications.ts).
export const PHONE_ATTR = '_tourism_phone';
export const EMAIL_ATTR = '_tourism_email';
// When the buyer accepted the Tourism Month terms (UTC, ISO) — shows on the order for staff.
export const TERMS_ATTR = '_tourism_terms';
// The buyer's name, asked on the cart page and passed on to the checkout's billing address — kept on the cart so the
// form is still filled when the buyer comes back to it.
export const FIRST_ATTR = '_tourism_first_name';
export const LAST_ATTR = '_tourism_last_name';
// "Go to payment" holds the cart's tickets for 5 minutes (NestApp tourism-hold.service.ts). The checkout's
// countdown (extension tourism-hold-timer) reads the end time; the order carries the id so the hold is released.
export const HOLD_ID_ATTR = '_tourism_hold_id';
export const HOLD_EXPIRES_ATTR = '_tourism_hold_expires';

const CART_FIELDS = `id checkoutUrl totalQuantity
  attributes { key value } buyerIdentity { email }
  cost { totalAmount { amount } }
  lines(first: 50) { nodes { id quantity attributes { key value }
    cost { totalAmount { amount } }
    merchandise { ... on ProductVariant { id price { amount } selectedOptions { name value }
      product { title handle featuredImage { url(transform: { maxWidth: 160 }) } } } } } }`;

async function gql(query, variables) {
  const res = await fetch(`/api/${version}/graphql.json`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ query, variables }),
  });
  if (!res.ok) throw new Error('network');
  const json = await res.json();
  if (json.errors?.length) throw new Error(json.errors[0].message);
  return json.data;
}

// localStorage can throw (private mode, blocked storage) — the cart then lives for this page only.
let memoryId = null;
const readId = () => { try { return localStorage.getItem(KEY); } catch { return memoryId; } };
const writeId = id => { memoryId = id; try { id ? localStorage.setItem(KEY, id) : localStorage.removeItem(KEY); } catch {} };

let lastCart = null; // the cart as last shown — what a line goes back to when Shopify trims it (keepUnlessFits)
function publish(cart) {
  lastCart = cart;
  const n = cart?.totalQuantity || 0;
  document.querySelectorAll('[data-tm-cart-count]').forEach(el => { el.textContent = n; el.hidden = n === 0; });
  document.querySelectorAll('[data-tm-cart-link]').forEach(el => el.setAttribute('aria-label', `סל ההזמנה, ${n} כרטיסים`));
  document.dispatchEvent(new CustomEvent('tm:cart', { detail: cart }));
  return cart;
}

function unwrap(payload) {
  const err = payload.userErrors?.[0] || payload.warnings?.[0];
  if (!payload.cart && err) throw new Error(err.message);
  return payload.cart;
}

// Not enough tickets left: Shopify still succeeds, trims the line to what is left and says so in a
// warning. The cart is published first (the count is right), then the caller tells the buyer.
const STOCK = ['MERCHANDISE_NOT_ENOUGH_STOCK', 'MERCHANDISE_OUT_OF_STOCK'];
function settle(payload) {
  const cart = publish(unwrap(payload));
  const w = payload.warnings?.find(x => STOCK.includes(x.code));
  if (w) throw Object.assign(new Error(w.message), { stock: w.code });
  return cart;
}

// Not enough tickets: Shopify keeps the line but trims it to what is left — showing that would tell the buyer
// how many are left. Buyers learn only "sold out" (owner, 2026-10-28), so the line goes back to what it was.
const qtyOf = (cart, match) => (cart?.lines.nodes || []).filter(match).reduce((n, l) => n + l.quantity, 0);
async function keepUnlessFits(payload, match, before) {
  const w = payload.warnings?.find(x => STOCK.includes(x.code));
  if (!w || !payload.cart) return settle(payload);
  const line = payload.cart.lines.nodes.find(match);
  if (line && line.quantity !== before) {
    const id = readId();
    const d = before
      ? await gql(`mutation($id: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $id, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } } }`, { id, lines: [{ id: line.id, quantity: before }] })
      : await gql(`mutation($id: ID!, $ids: [ID!]!) { cartLinesRemove(cartId: $id, lineIds: $ids) { cart { ${CART_FIELDS} } userErrors { message } } }`, { id, ids: [line.id] });
    publish(unwrap(d.cartLinesUpdate || d.cartLinesRemove));
  } else publish(payload.cart);
  throw Object.assign(new Error(w.message), { stock: w.code });
}

export async function getCart() {
  const id = readId();
  if (!id) return publish(null);
  const { cart } = await gql(`query($id: ID!) { cart(id: $id) { ${CART_FIELDS} } }`, { id });
  if (!cart) writeId(null); // expired or completed → start fresh next time
  return publish(cart);
}

// After the demo "payment" — like a real order, the cart starts empty again.
export function forgetCart() {
  writeId(null);
  return publish(null);
}

// A line's Activity day and Branch, for showing: the day variant's options — the day is the value shaped like a day's
// name ("4 בנובמבר 2026", whatever the option is called), the Branch the other option when the stock is per Branch.
const DAY_NAME = /^\d{1,2} ב\S+ \d{4}$/;
export function lineDay(l) {
  const opts = l.merchandise?.selectedOptions || [];
  const attr = k => l.attributes.find(a => a.key === k)?.value || '';
  const day = opts.find(o => DAY_NAME.test(o.value));
  if (!day) return { date: attr(DATE_ATTR), branch: attr(BRANCH_ATTR) };
  return { date: day.value, branch: opts.find(o => o !== day)?.value || '' };
}

export async function addLine({ variantId, quantity, region }) {
  const attributes = [{ key: '_tourism', value: '1' }];
  if (region) attributes.push({ key: REGION_ATTR, value: region });
  const lines = [{ merchandiseId: variantId, quantity, attributes }];
  const match = l => l.merchandise.id === variantId && (l.attributes.find(a => a.key === REGION_ATTR)?.value || '') === (region || '');
  let id = readId();
  if (id) {
    const before = qtyOf(lastCart, match);
    const d = await gql(`mutation($id: ID!, $lines: [CartLineInput!]!) { cartLinesAdd(cartId: $id, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } warnings { code message } } }`, { id, lines });
    if (d.cartLinesAdd.cart) return keepUnlessFits(d.cartLinesAdd, match, before);
    writeId(null); // stale id — fall through and create a new cart
  }
  const d = await gql(`mutation($lines: [CartLineInput!]!) { cartCreate(input: { lines: $lines }) { cart { ${CART_FIELDS} } userErrors { message } warnings { code message } } }`, { lines });
  if (d.cartCreate.cart) writeId(d.cartCreate.cart.id);
  return keepUnlessFits(d.cartCreate, match, 0);
}

export async function updateLine(lineId, quantity) {
  const match = l => l.id === lineId;
  const before = qtyOf(lastCart, match);
  const d = await gql(`mutation($id: ID!, $lines: [CartLineUpdateInput!]!) { cartLinesUpdate(cartId: $id, lines: $lines) { cart { ${CART_FIELDS} } userErrors { message } warnings { code message } } }`,
    { id: readId(), lines: [{ id: lineId, quantity }] });
  return keepUnlessFits(d.cartLinesUpdate, match, before);
}

export async function removeLine(lineId) {
  const d = await gql(`mutation($id: ID!, $ids: [ID!]!) { cartLinesRemove(cartId: $id, lineIds: $ids) { cart { ${CART_FIELDS} } userErrors { message } } }`,
    { id: readId(), ids: [lineId] });
  return publish(unwrap(d.cartLinesRemove));
}

// Saves where the voucher goes on the cart — phone, email or both ('' = not given; the app then falls
// back to the checkout contact) plus when the terms were accepted — and pre-fills the checkout email when there is one.
export async function setContact({ phone, email, termsAt, firstName, lastName, hold }) {
  const id = readId();
  const a = await gql(`mutation($id: ID!, $attrs: [AttributeInput!]!) { cartAttributesUpdate(cartId: $id, attributes: $attrs) { cart { ${CART_FIELDS} } userErrors { message } } }`,
    { id, attrs: [{ key: PHONE_ATTR, value: phone }, { key: EMAIL_ATTR, value: email }, { key: TERMS_ATTR, value: termsAt },
      { key: FIRST_ATTR, value: firstName }, { key: LAST_ATTR, value: lastName },
      { key: HOLD_ID_ATTR, value: hold?.holdId || '' }, { key: HOLD_EXPIRES_ATTR, value: hold?.expiresAt || '' }] });
  const cart = unwrap(a.cartAttributesUpdate);
  if (!email) return publish(cart);
  const b = await gql(`mutation($id: ID!, $email: String!) { cartBuyerIdentityUpdate(cartId: $id, buyerIdentity: { email: $email, countryCode: IL }) { cart { ${CART_FIELDS} } userErrors { message } } }`,
    { id, email });
  return publish(unwrap(b.cartBuyerIdentityUpdate));
}

// Holds the cart's tickets for 5 minutes, or says which days have fewer free tickets than the cart wants (never how many):
// { ok: true, holdId, expiresAt } | { ok: false, soldOut: [{ variantId }] }. Throws when the app can't be reached.
export async function holdTickets(cart) {
  const res = await fetch('/apps/api/tourism-hold', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cartId: cart.id, lines: cart.lines.nodes.map(l => ({ variantId: Number(l.merchandise.id.split('/').pop()), quantity: l.quantity })) }),
  });
  if (!res.ok) throw new Error('hold');
  return res.json();
}

export function toast(message, { href, label, error = false } = {}) {
  const el = document.getElementById('tm-toast');
  if (!el) return;
  el.querySelector('span').textContent = message;
  const a = el.querySelector('a');
  a.hidden = !href;
  if (href) { a.href = href; a.textContent = label; }
  el.classList.toggle('tm-toast--error', error);
  el.classList.add('is-show');
  clearTimeout(toast.t);
  toast.t = setTimeout(() => el.classList.remove('is-show'), 3600);
}

export function cartUrl() {
  return document.querySelector('[data-tm-cart-link]')?.getAttribute('href') || '?view=tayarot-cart';
}

getCart().catch(() => {}); // header count on every page; a failure just leaves it hidden
